import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ShopService } from '@services/shops/shop.service';
import { ExtensionComponentType, ICheckoutExtensions } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { Store } from '@ngrx/store';
import { Actions as NgRxActions, ofType } from '@ngrx/effects';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import { CheckoutActions } from '@pages/checkout-app/checkout-store/actions';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
import { Options } from '@pages/checkout-app/types';
import {
  map,
  takeUntil,
  switchMap,
  filter,
  tap,
  take,
  startWith,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs/operators';
import { onDestroyComplete } from '@app/sharedModule/decorators/on-destroy-unsubscribe.decorator';
import { Observable, Subject, forkJoin, of, combineLatest } from 'rxjs';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DeleteExtensionDialog } from '../components/shared/delete/delete-extension-dialog';
import { ArchiveExtensionDialog } from '../components/shared/archive/archive-extension-dialog';
import {
  ToggleExtensionGroupsDialog,
  ToggleExtensionGroupsDialogResult,
} from '../components/shared/toggle-extension-groups/toggle-extension-groups-dialog';
import { PdqPopoverDirective } from '@app/storybook/components/popover/popover.directive';
import { ToastOptions } from '@app/storybook/services/toast/toast-types';
import { ExtComponentTypesOptions } from '../components/shared/extension-header/config';
import { IExtensionGroup } from '../../extension-groups/models/extension-group.models';
import { ExtensionsApiService } from '@pages/checkout-app/services/api/extensions.api.service';
import { TypeToRouteSlugMap, ExtensionsUsingIdInUrl } from '../types';
import { isDynamicExtension } from '../models';
import { resolveShowAutonomousMark } from '../components/extension-components/checkout-upsell/autonomous-discount';
import { FeatureFlag } from '@app/core/services/feature-flags/feature-flag.model';
import { FeatureFlagService } from '@app/core/services/feature-flags/feature-flag.service';
enum Actions {
  EDIT = 'edit',
  DELETE = 'delete',
  DUPLICATE = 'duplicate',
  ARCHIVE = 'archive',
}

const NOT_LIVE_DAYS_THRESHOLD = 30;

const ActionOptions: Options[] = [
  {
    label: 'Edit',
    value: Actions.EDIT,
  },
  {
    label: 'Duplicate',
    value: Actions.DUPLICATE,
  },
  {
    label: 'Delete',
    value: Actions.DELETE,
  },
  {
    label: 'Archive',
    value: Actions.ARCHIVE,
  },
];

@Component({
  selector: 'checkout-extensions-list',
  templateUrl: './checkout-extensions-list.component.html',
  styleUrls: ['./checkout-extensions-list.component.scss'],
  standalone: false,
})
export class CheckoutExtensionsListComponent implements OnInit, OnDestroy {
  @ViewChild(PdqPopoverDirective) popoverDirective: PdqPopoverDirective;
  @onDestroyComplete() destroy$ = new Subject();
  extComponentTypesOptions = ExtComponentTypesOptions;
  notLiveDaysThreshold = NOT_LIVE_DAYS_THRESHOLD;
  searchControl = new FormControl<string>('', { nonNullable: true });
  searchTerm = '';
  autonomousDiscountFlagOn = false;
  extensionsList$ = this.store.select(fromCheckoutReducer.selectExtensionsForListWithLiveStatus);
  filteredExtensionsList$ = combineLatest([
    this.extensionsList$,
    this.searchControl.valueChanges.pipe(debounceTime(200), distinctUntilChanged(), startWith('')),
  ]).pipe(map(([extensions, term]) => this.filterExtensions(extensions, term)));
  extensionsListLoading$ = this.store.select(fromCheckoutReducer.selectExtensionsListLoading);
  actionOptions = ActionOptions;
  extensionComponentType = ExtensionComponentType;
  actionEnum = Actions;
  validExtensionTypes$: Observable<string[]>;
  extensionGroups: IExtensionGroup[] = [];

  constructor(
    private shopService: ShopService,
    private store: Store<fromCheckoutReducer.State>,
    private extensionsService: ExtensionsService,
    private dialog: MatDialog,
    private extensionsApiService: ExtensionsApiService,
    private router: Router,
    private actions$: NgRxActions,
    private featureFlags: FeatureFlagService,
  ) {}

  ngOnDestroy() {}

  ngOnInit() {
    this.extensionsService.setCurrentExtensionType(ExtensionComponentType.LIST);
    this.validExtensionTypes$ = this.extensionsService.extensionRequirements$.pipe(
      map((res) => {
        return res.map((val) => val.status === 'valid' && val.type);
      }),
    );

    this.store
      .select(fromCheckoutReducer.selectExtensionGroups)
      .pipe(takeUntil(this.destroy$))
      .subscribe((groups) => {
        this.extensionGroups = groups || [];
      });

    this.searchControl.valueChanges
      .pipe(startWith(''), takeUntil(this.destroy$))
      .subscribe((value) => (this.searchTerm = (value ?? '').trim()));

    this.featureFlags
      .isEnabled$(FeatureFlag.AutonomousUpsellDiscount)
      .pipe(takeUntil(this.destroy$))
      .subscribe((on) => (this.autonomousDiscountFlagOn = on));

    this.store.dispatch(CheckoutActions.loadExtensionGroups());
  }

  onComponentTypeSelect(type: ExtensionComponentType) {
    this.store.dispatch(CheckoutActions.resetActiveExtension());
    const routeSlug = TypeToRouteSlugMap[type];
    if (routeSlug) {
      this.router.navigate(['/convert/extensions', routeSlug, 'new']);
    }
  }

  formatLabel(value: string): string {
    if (value === ExtensionComponentType.DYNAMIC_EXTENSION) return 'Dynamic Extension';
    return this.extComponentTypesOptions.find((ex) => ex.value === value)?.label ?? '';
  }

  isAutonomousDiscount(extension: ICheckoutExtensions): boolean {
    return resolveShowAutonomousMark(this.autonomousDiscountFlagOn, extension.template?.discountConfiguration?.mode);
  }

  getExtensionDisplayName(extension: ICheckoutExtensions): string {
    if (extension.type === ExtensionComponentType.CHECKOUT_UPSELL) {
      return extension.template?.title || extension.template?.internalName || 'Untitled';
    }
    if (isDynamicExtension(extension)) {
      return extension.template?.name || extension.template?.internalName || extension.template?.title || 'Untitled';
    }
    return extension.template?.internalName || extension.template?.title || 'Untitled';
  }

  onEnableExtension(extension: ICheckoutExtensions, value: boolean) {
    // When toggling ON, check if extension is in any groups
    if (value) {
      const groupsContainingExtension = this.getGroupsContainingExtension(extension.id);
      if (groupsContainingExtension.length > 0) {
        this.openToggleExtensionGroupsDialog(extension);
        return;
      }
    }

    // If toggling OFF or extension is not in any groups, proceed normally
    this.updateExtensionEnabled(extension.id, value);
  }

  private getGroupsContainingExtension(extensionId: number): IExtensionGroup[] {
    return this.extensionGroups.filter((group) =>
      group.groupComponents?.some((component) => component.extensionComponentId === extensionId),
    );
  }

  private openToggleExtensionGroupsDialog(extension: ICheckoutExtensions): void {
    const dialogRef = this.dialog.open(ToggleExtensionGroupsDialog, {
      data: {
        extension,
        extensionGroups: this.extensionGroups,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        takeUntil(this.destroy$),
        tap((result) => {
          if (!result) {
            this.store.dispatch(CheckoutActions.loadExtensionsListLightweight());
          }
        }),
        filter((result): result is ToggleExtensionGroupsDialogResult => !!result),
        tap((result) => {
          // Dispatch NgRx action to update extension
          this.store.dispatch(
            CheckoutActions.updateExtension({
              id: extension.id,
              extension: { enabled: result.enabled, id: extension.id } as any,
            }),
          );
        }),
        switchMap((result) =>
          this.actions$.pipe(
            ofType(CheckoutActions.updateExtensionSuccess),
            filter((action) => action.extension.id === extension.id),
            take(1),
            switchMap(() => {
              // Group selections by groupId
              const selectionsByGroup = new Map<number, { componentId: number; enabled: boolean }[]>();
              result.groupSelections.forEach((selection) => {
                if (!selectionsByGroup.has(selection.groupId)) {
                  selectionsByGroup.set(selection.groupId, []);
                }
                selectionsByGroup.get(selection.groupId).push({
                  componentId: selection.componentId,
                  enabled: selection.selected,
                });
              });

              if (selectionsByGroup.size === 0) {
                return of(null);
              }

              const updateRequests = Array.from(selectionsByGroup.entries()).map(([groupId, components]) =>
                this.toggleExtensionsInGroup(groupId, components),
              );
              return forkJoin(updateRequests);
            }),
          ),
        ),
      )
      .subscribe({
        next: () => {
          this.store.dispatch(CheckoutActions.loadExtensionGroups());
          const options: ToastOptions = {
            message: 'Extension settings updated',
            mode: 'success',
            position: 'top',
            duration: 4000,
            closeable: true,
          };
          this.extensionsService.createToast(options);
        },
        error: (err) => {
          console.log(err);
        },
      });
  }

  private updateExtensionEnabled(extensionId: number, enabled: boolean): void {
    const partialExtensionObject = {
      enabled,
      id: extensionId,
    };

    // Dispatch NgRx action to update extension (will update state via reducer)
    this.store.dispatch(
      CheckoutActions.updateExtension({
        id: extensionId,
        extension: partialExtensionObject as any,
      }),
    );

    // Handle extension groups if disabling
    if (!enabled) {
      this.actions$
        .pipe(
          ofType(CheckoutActions.updateExtensionSuccess),
          filter((action) => action.extension.id === extensionId),
          take(1),
          switchMap(() => this.disableExtensionInAllExtensionGroups(extensionId)),
        )
        .subscribe(() => {
          this.store.dispatch(CheckoutActions.loadExtensionGroups());
        });
    }
  }

  private disableExtensionInAllExtensionGroups(extensionId: number): Observable<any> {
    // Group components by groupId
    const componentsByGroup = new Map<number, number[]>();

    this.extensionGroups.forEach((group) => {
      group.groupComponents?.forEach((component) => {
        if (component.extensionComponentId === extensionId && component.enabled) {
          if (!componentsByGroup.has(group.id)) {
            componentsByGroup.set(group.id, []);
          }
          componentsByGroup.get(group.id).push(component.id);
        }
      });
    });

    if (componentsByGroup.size === 0) {
      return of(null);
    }

    const totalComponents = Array.from(componentsByGroup.values()).reduce((sum, arr) => sum + arr.length, 0);

    // Disable all components per group using batch endpoint
    try {
      const disableRequests = Array.from(componentsByGroup.entries()).map(([groupId, componentIds]) =>
        this.toggleExtensionsInGroup(
          groupId,
          componentIds.map((id) => ({ componentId: id, enabled: false })),
        ),
      );
      return forkJoin(disableRequests);
    } finally {
      const options: ToastOptions = {
        message: 'Turned off in ' + totalComponents + ' extension groups',
        mode: 'partial_success',
        position: 'top',
        duration: 4000,
        closeable: true,
      };
      this.extensionsService.createToast(options);
    }
  }

  private toggleExtensionsInGroup(
    groupId: number,
    components: { componentId: number; enabled: boolean }[],
  ): Observable<any> {
    return this.extensionsApiService.patch(`admin/v1/extension-groups/${groupId}/components/update`, {
      components: components.map((c) => ({ id: c.componentId, enabled: c.enabled })),
    });
  }

  copyToken(ext: ICheckoutExtensions): void {
    if (!ext?.usageToken) {
      return;
    }
    navigator.clipboard
      .writeText(ext.usageToken)
      .then(() =>
        this.extensionsService.createToast({
          message: 'Token copied to clipboard',
          mode: 'info',
          position: 'bottom',
          duration: 4000,
          closeable: true,
        }),
      )
      .catch(() =>
        this.extensionsService.createToast({
          message: 'Failed to copy token',
          mode: 'danger',
          position: 'bottom',
          duration: 4000,
          closeable: true,
        }),
      );
  }

  onActionOptionSelect(optionValue: Actions, ext: ICheckoutExtensions) {
    const extensionId = ext?.id;
    const extensionType = ext?.type as ExtensionComponentType;
    switch (optionValue) {
      case Actions.EDIT: {
        const routeSlug = TypeToRouteSlugMap[ext?.type];
        if (routeSlug) {
          const useIdInUrl = ExtensionsUsingIdInUrl.includes(ext?.type);
          const urlParam = useIdInUrl ? ext.id.toString() : ext.usageToken;
          if (urlParam) {
            this.router.navigate(['/convert/extensions', routeSlug, urlParam]);
          }
        }
        const metricsFromDate = new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().slice(0, 10);
        const metricsToDate = new Date().toISOString().slice(0, 10);
        this.store.dispatch(
          CheckoutActions.loadExtensionById({
            shopId: this.shopService.shopId,
            metricsToDate,
            metricsFromDate,
            extensionId,
            extensionType,
          }),
        );
        break;
      }

      case Actions.DUPLICATE: {
        this.store.dispatch(CheckoutActions.duplicateExtension({ extensionId }));
        break;
      }
      case Actions.DELETE: {
        this.dialog.open(DeleteExtensionDialog, {
          data: {
            extensionId: extensionId,
          },
        });
        break;
      }
      case Actions.ARCHIVE: {
        this.dialog.open(ArchiveExtensionDialog, {
          data: { extensionId },
        });
        break;
      }
    }
  }

  private filterExtensions(
    extensions: fromCheckoutReducer.ExtensionListItem[],
    term: string,
  ): fromCheckoutReducer.ExtensionListItem[] {
    const normalizedTerm = term?.trim().toLowerCase();
    if (!normalizedTerm) {
      return extensions ?? [];
    }
    return (extensions ?? []).filter((ext) => {
      const name = this.getExtensionDisplayName(ext).toLowerCase();
      const token = (ext.usageToken ?? '').toLowerCase();
      return name.includes(normalizedTerm) || token.includes(normalizedTerm);
    });
  }
}
